import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  Box,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Badge,
  Fab,
} from "@mui/material";
import { Add, ArrowBack, FilterList, Close, Refresh, FilterAlt, Edit } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import EditDelete from "../../../../components/EditDelete";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import useInventory from "../../../../hooks/useInventory";
import axios from "../../../../utils/axios";
import DatePicker from "react-multi-date-picker";
import ToolBar from "../../../../components/Toolbar";
import { saveFile } from "../../../../utils/common";

function IssueSection() {
  const { getAllIssue, addIssue, updateIssue, autoSearchUser, exportInventoryReport, isLoading } =
    useInventory();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isAdding, setIsAdding] = useState(false);
  const [materials, setMaterials] = useState([]);

  // Export State
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  // User Autocomplete State
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form State
  const [issueNo, setIssueNo] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([{ materialId: "", issueQuantity: 1 }]);
  const [editingId, setEditingId] = useState(null);

  // Filter State
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterIssueDate, setFilterIssueDate] = useState("");
  const [filterUserName, setFilterUserName] = useState("");
  const [filterMaterialName, setFilterMaterialName] = useState("");

  // Temp/Modal Filter State
  const [tempIssueDate, setTempIssueDate] = useState("");
  const [tempUserName, setTempUserName] = useState("");
  const [tempMaterialName, setTempMaterialName] = useState("");

  const fetchIssues = async (
    date = filterIssueDate,
    user = filterUserName,
    material = filterMaterialName,
  ) => {
    try {
      const activeFilters = [];
      if (date) {
        activeFilters.push({
          fieldName: "issueDate",
          fieldValue: date,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (user.trim()) {
        activeFilters.push({
          fieldName: "userName",
          fieldValue: user.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (material.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: material.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }

      const payload = {
        pagination: { pageNumber: paginationModel.page + 1, pageSize: paginationModel.pageSize },
        filters: activeFilters,
        sortFields: [{ field: "issueId", sort: "desc" }],
      };
      const res = await getAllIssue(payload);
      if (res) {
        setRows(res.data || []);
        setRowCount(res.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMaterials = async () => {
    try {
      const res = await axios.post("/material/list", {
        pagination: { pageNumber: 1, pageSize: 1000 },
        filters: [],
        sortFields: [],
      });
      setMaterials(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [paginationModel, filterIssueDate, filterUserName, filterMaterialName]);

  useEffect(() => {
    if (isAdding) {
      fetchMaterials();
    }
  }, [isAdding]);

  const handleUserSearch = async (val) => {
    if (!val || val.length < 2) return;
    setUserLoading(true);
    try {
      const results = await autoSearchUser(val);
      setUserOptions(results);
    } catch (e) {
      console.error(e);
    } finally {
      setUserLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = items.map((item, idx) => {
      if (idx === index) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setItems(updated);
  };

  const dispatch = useDispatch();

  const handleEdit = (row) => {
    setIssueNo(row.issueNo || "");
    setIssueDate(
      row.issueDate ? row.issueDate.split(" ")[0] : new Date().toISOString().split("T")[0],
    );
    setSelectedUser(row.userId ? { radUserId: row.userId, userName: row.userName } : null);
    setRemarks(row.remarks || "");
    setItems(
      row.items && row.items.length > 0
        ? row.items.map((i) => ({
            materialId: i.materialId,
            issueQuantity: i.issueQuantity,
          }))
        : [{ materialId: "", issueQuantity: 1 }],
    );
    setEditingId(row.issueId);
    setIsAdding(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      dispatch(showErrorSnackbar("User is required"));
      return;
    }
    const hasInvalidItem = items.some((item) => !item.materialId || item.issueQuantity <= 0);
    if (hasInvalidItem) {
      dispatch(showErrorSnackbar("Please select a material and enter a valid quantity."));
      return;
    }

    try {
      const payload = {
        issueNo,
        issueDate,
        userId: parseInt(selectedUser.radUserId),
        remarks,
        items: items.map((i) => ({
          materialId: parseInt(i.materialId),
          issueQuantity: parseFloat(i.issueQuantity),
        })),
      };
      if (editingId) {
        await updateIssue(editingId, payload);
        dispatch(showSuccessSnackbar("Material issue updated successfully!"));
      } else {
        await addIssue(payload);
        dispatch(showSuccessSnackbar("Material issued successfully!"));
      }
      setIsAdding(false);
      setEditingId(null);
      fetchIssues();
      // Reset form
      setIssueNo("");
      setSelectedUser(null);
      setRemarks("");
      setItems([{ materialId: "", issueQuantity: 1 }]);
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to save issue material"));
    }
  };

  const handleDownloadFile = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const activeFilters = [];
      if (filterIssueDate) {
        activeFilters.push({
          fieldName: "issueDate",
          fieldValue: filterIssueDate,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterUserName.trim()) {
        activeFilters.push({
          fieldName: "userName",
          fieldValue: filterUserName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterMaterialName.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: filterMaterialName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      const payload = {
        filters: activeFilters,
      };

      const resBlob = await exportInventoryReport("issue", fileType, payload);
      const filename = `issue_list_${new Date().toISOString().slice(0, 10)}.${fileType}`;
      await saveFile(resBlob, filename);
      dispatch(showSuccessSnackbar(`Report exported successfully as ${fileType.toUpperCase()}!`));
    } catch (err) {
      console.error(err);
      dispatch(showErrorSnackbar("Failed to export report"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  const handleApplyFilter = () => {
    setFilterIssueDate(tempIssueDate);
    setFilterUserName(tempUserName);
    setFilterMaterialName(tempMaterialName);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setFilterIssueDate("");
    setFilterUserName("");
    setFilterMaterialName("");
    setTempIssueDate("");
    setTempUserName("");
    setTempMaterialName("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setIsFilterOpen(false);
  };

  const handleRefresh = () => {
    fetchIssues();
  };

  const activeFiltersCount =
    (filterIssueDate ? 1 : 0) + (filterUserName ? 1 : 0) + (filterMaterialName ? 1 : 0);

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <EditDelete
          rowId={params.row.issueId}
          editPage={() => handleEdit(params.row)}
          menus={["edit"]}
        />
      ),
    },
    { field: "issueNo", headerName: "Issue No", width: 180 },
    { field: "issueDate", headerName: "Issue Date", width: 180 },
    { field: "userName", headerName: "User", width: 220 },
    {
      field: "materialName",
      headerName: "Material Name",
      width: 220,
      valueGetter: (value, row) => {
        if (!row?.items || row.items.length === 0) return "";
        return row.items
          .map((item) => item.materialName || "")
          .filter(Boolean)
          .join(", ");
      },
    },
    {
      field: "totalQuantity",
      headerName: "Total Quantity",
      width: 160,
      type: "number",
      valueGetter: (value, row) => {
        if (!row?.items) return 0;
        return row.items.reduce((sum, item) => sum + (item.issueQuantity || 0), 0);
      },
    },
    { field: "remarks", headerName: "Remarks", width: 300 },
  ];

  if (isAdding) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h5">{editingId ? "Edit Issue" : "Issue Material"}</Typography>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              setIsAdding(false);
              setEditingId(null);
              setIssueNo("");
              setSelectedUser(null);
              setRemarks("");
              setItems([{ materialId: "", issueQuantity: 1 }]);
            }}
          >
            Back to List
          </Button>
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <DatePicker
                containerStyle={{ width: "100%" }}
                portal
                zIndex={10000}
                value={issueDate}
                onChange={(date) => setIssueDate(date ? date.format("YYYY-MM-DD") : "")}
                format="YYYY/MM/DD"
                render={
                  <TextField
                    fullWidth
                    label="Issue Date"
                    required
                    InputLabelProps={{ shrink: !!issueDate }}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={userOptions}
                getOptionLabel={(option) => option.userName || ""}
                loading={userLoading}
                value={selectedUser}
                onChange={(e, newVal) => setSelectedUser(newVal)}
                onInputChange={(e, val) => handleUserSearch(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search User"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {userLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {items.map((item, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12} md={6}>
                  <Select
                    fullWidth
                    value={item.materialId}
                    onChange={(e) => handleItemChange(index, "materialId", e.target.value)}
                    displayEmpty
                    required
                  >
                    <MenuItem value="" disabled>
                      Select Material
                    </MenuItem>
                    {materials.map((m) => (
                      <MenuItem key={m.materialId} value={m.materialId}>
                        {m.materialName} ({m.uom})
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Issue Quantity"
                    inputProps={{ min: "1", step: "1" }}
                    value={item.issueQuantity}
                    onChange={(e) => handleItemChange(index, "issueQuantity", e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    required
                  />
                </Grid>
              </React.Fragment>
            ))}

            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                size="large"
              >
                {isLoading ? "Saving..." : "Save Issue"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h5">Material Issue Log</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <ToolBar
            addPage={() => setIsAdding(true)}
            refreshPage={handleRefresh}
            openFilterDialog={() => setIsFilterOpen(true)}
            downloadFile={handleDownloadFile}
            isLoadingDownload={isDownloading}
            downloadFileType={downloadType}
          />
        </Box>
      </Box>
      <div style={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.issueId}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={isLoading}
          disableRowSelectionOnClick
        />
      </div>

      {/* Advance Filter Dialog */}
      <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Advance Filter</Typography>
          <IconButton onClick={() => setIsFilterOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="User"
                value={tempUserName}
                onChange={(e) => setTempUserName(e.target.value)}
                placeholder="Search user name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Material Name"
                value={tempMaterialName}
                onChange={(e) => setTempMaterialName(e.target.value)}
                placeholder="Search material name"
              />
            </Grid>
            <Grid item xs={12}>
              <DatePicker
                containerStyle={{ width: "100%" }}
                portal
                zIndex={10000}
                range
                value={tempIssueDate ? tempIssueDate.split(",") : []}
                onChange={(dates) => {
                  if (dates && dates.length > 0) {
                    const formatted = dates.map((d) => d.format("YYYY-MM-DD")).join(",");
                    setTempIssueDate(formatted);
                  } else {
                    setTempIssueDate("");
                  }
                }}
                format="YYYY/MM/DD"
                render={
                  <TextField
                    fullWidth
                    label="Issue Date"
                    variant="outlined"
                    InputLabelProps={{ shrink: !!tempIssueDate }}
                  />
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleResetFilter} variant="outlined" startIcon={<Refresh />}>
            Reset
          </Button>
          <Button
            onClick={handleApplyFilter}
            variant="contained"
            color="primary"
            startIcon={<FilterAlt />}
          >
            filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default IssueSection;
